import { first } from 'lodash';

import {
  getSeriesItemColor,
  getSeriesItemOptionsByKey,
  getVisualisationDefaultChartType,
  getVisualisationOptions,
  getVisualisationOrientation,
  getVisualisationShowTotals,
  isVisualisationStacked,
  setVisualisationOptions,
  setVisualisationOrientationOption,
  setVisualisationShowTotalsOption,
  setVisualisationStackedOption,
} from '@/core/visualisation';

import { ButtonGroup } from '@/components/button';

import { useExplorationContext } from '../exploration/exploration-context';
import {
  AggregatedVisualisation,
  Metric,
  Model,
  Pipeline,
  QueryVariables,
  SimpleVisualisation,
  Visualisation,
  VisualisationOrientation,
  isAggregatedVisualisation,
} from '../types';
import { Toggle } from '../../components/form/toggle';
import {
  disableShowValues,
  enableShowValues,
  getActiveSeriesAxes,
  getActiveSeriesChartTypes,
  getActiveSeriesShowValues,
  getBigNumberFields,
  getChartType,
  getDefaultSortForVisualisation,
  getQueryForVisualisation,
  isBigNumberToggleable,
  isNumericField,
  resetSeriesOptions,
  setBigNumberOptions,
  setSecondaryAxisKey,
  setSeriesChartType,
  setSeriesColor,
  setVisualisationValueKeys,
  toggleVisualisationAggregation,
} from '../components/visualisation/utils';
import { getPipelineFields } from '../pipeline/state';
import { EditAggregatedVisualisation } from './edit-aggregated-visualisation';
import { EditSimpleVisualisation } from './edit-simple-visualisation';
import { EditBigNumberVisualisation } from './edit-big-number-visualisation';

import { buildSeriesMenuItems } from './utils';
import { SortingEditor } from '../edit-pipeline/sorting-editor';

import form from '../../components/form/form.module.scss';

interface EditVisualisationProps {
  visualisation: Visualisation;
  pipeline: Pipeline;
  models: Model[];
  metrics: Metric[];
  variables: QueryVariables;
  onChange: (visualisation: Visualisation) => void;
}

export const EditVisualisation = (props: EditVisualisationProps) => {
  const { visualisation, pipeline, models, metrics, variables, onChange } = props;
  const visualisationOptions = getVisualisationOptions(visualisation);
  const { exploration } = useExplorationContext();

  const isAggregated = isAggregatedVisualisation(visualisation);
  const stateContext = { models, variables, metrics, exploration };
  const fields = getPipelineFields(pipeline, stateContext);
  const options = visualisation.viewOptions ?? {};
  const bigNumberOptions = options.bigNumber;
  const bigNumberFieldKey = bigNumberOptions?.key;
  const { visualisationPipeline, groups } = getQueryForVisualisation({
    pipeline,
    fields,
    visualisation,
    exploration,
  });
  const isBigNumberVisible = bigNumberFieldKey !== undefined;
  const visualisationFields = getPipelineFields(visualisationPipeline, stateContext);
  const chartType = getChartType(visualisation, fields);

  const handleAggregationToggleChange = (checked: boolean) => {
    onChange(toggleVisualisationAggregation(checked, visualisation, fields));
  };

  const handleBigNumberToggleChange = (checked: boolean) => {
    const key = checked ? first(getBigNumberFields(visualisationFields))?.key : undefined;

    onChange({
      ...visualisation,
      viewOptions: setBigNumberOptions(
        { bigNumber: key !== undefined ? { key } : undefined },
        visualisation.viewOptions,
        {
          mainAxisKey: visualisation.mainAxisKey,
        },
      ),
    });
  };

  const handleSortingToggleChange = (checked: boolean) => {
    onChange({
      ...visualisation,
      sort: checked
        ? getDefaultSortForVisualisation(visualisation, visualisationFields)
        : undefined,
    });
  };

  const handleStackedChange = (stacked: boolean) => {
    onChange(
      setVisualisationOptions(
        visualisation,
        setVisualisationStackedOption(visualisationOptions, stacked),
      ),
    );
  };

  const handleShowTotalsChange = (showTotals: boolean) => {
    onChange(
      setVisualisationOptions(
        visualisation,
        setVisualisationShowTotalsOption(visualisationOptions, showTotals),
      ),
    );
  };

  const handleOrientationChange = (orientation: VisualisationOrientation) => {
    onChange(
      setVisualisationOptions(
        visualisation,
        setVisualisationOrientationOption(visualisationOptions, orientation),
      ),
    );
  };

  const hasCombinedSeries = groups.length > 1;
  const hasMoreThanOneSeries = visualisation.valueKeys.length > 1 || hasCombinedSeries;

  const getValueKeyMenuOptions = (key: string, idx: number) => {
    const defaultChartType = getVisualisationDefaultChartType(visualisation, fields);
    const seriesOptions = getSeriesItemOptionsByKey(visualisationOptions, key);
    return buildSeriesMenuItems({
      color: getSeriesItemColor(seriesOptions, idx),
      chartTypes: getActiveSeriesChartTypes(key, visualisationOptions, defaultChartType),
      axes: getActiveSeriesAxes(key, visualisationOptions),
      showValues: getActiveSeriesShowValues(key, visualisationOptions),
      isChartTypeDisabled: false,
      isSecondaryAxisDisabled: !['grouped-timeseries', 'bar'].includes(chartType),
      onShowValuesChange: (showValues) => {
        onChange({
          ...visualisation,
          viewOptions: showValues
            ? enableShowValues(key, visualisationOptions)
            : disableShowValues(key, visualisationOptions),
        });
      },
      onColorChange: !hasCombinedSeries
        ? (color) =>
            onChange({
              ...visualisation,
              viewOptions: setSeriesColor(key, color, visualisation.viewOptions),
            })
        : undefined,
      onChartTypeChange: (chartType) =>
        onChange({
          ...visualisation,
          viewOptions: setSeriesChartType(key, chartType, visualisation.viewOptions),
        }),
      onSecondaryAxisChange: (checked) => {
        onChange({
          ...visualisation,
          viewOptions: setSecondaryAxisKey(key, checked, visualisation.viewOptions),
        });
      },
      onRemove:
        !isAggregated && visualisation.valueKeys.length > 1
          ? () =>
              onChange(
                setVisualisationValueKeys(
                  visualisation,
                  visualisation.valueKeys.filter((k) => k !== key),
                ),
              )
          : undefined, //Aggregation editor handles removing
      onReset: () => {
        onChange({
          ...visualisation,
          viewOptions: resetSeriesOptions(key, visualisation.viewOptions),
        });
      },
    });
  };

  const isStacked = isVisualisationStacked(visualisationOptions, hasMoreThanOneSeries);
  const orientation = getVisualisationOrientation(visualisation, fields);

  return (
    <div className={form.formHorizontal}>
      <Toggle
        checked={isAggregated}
        disabled={!fields.some(isNumericField)}
        onChange={handleAggregationToggleChange}
        size="small">
        Aggregate
      </Toggle>
      {isAggregated ? (
        <EditAggregatedVisualisation
          visualisation={visualisation as AggregatedVisualisation}
          pipeline={pipeline}
          exploration={exploration}
          models={models}
          metrics={metrics}
          variables={variables}
          getValueKeyMenuOptions={getValueKeyMenuOptions}
          onChange={onChange}
        />
      ) : (
        <EditSimpleVisualisation
          visualisation={visualisation as SimpleVisualisation}
          pipeline={pipeline}
          exploration={exploration}
          models={models}
          metrics={metrics}
          variables={variables}
          getValueKeyMenuOptions={getValueKeyMenuOptions}
          groups={groups}
          onChange={onChange}
        />
      )}
      <hr className={form.dashed} />
      <Toggle
        checked={visualisation.sort !== undefined}
        disabled={chartType !== 'bar'}
        onChange={handleSortingToggleChange}
        size="small">
        Sort manually
      </Toggle>
      {visualisation.sort !== undefined && (
        <div className={form.formRow}>
          <label className={form.formLabel}>Sorted by</label>
          <SortingEditor
            fields={visualisationFields}
            sorting={visualisation.sort}
            setSorting={(sort) => {
              onChange({
                ...visualisation,
                sort,
              });
            }}
          />
        </div>
      )}
      <hr className={form.dashed} />
      <Toggle
        checked={isBigNumberVisible}
        disabled={!isBigNumberToggleable(visualisation, visualisationFields, groups)}
        onChange={handleBigNumberToggleChange}
        size="small">
        Show latest value as big number
      </Toggle>
      {isBigNumberVisible ? (
        <EditBigNumberVisualisation
          visualisation={visualisation}
          fields={fields}
          visualisationFields={visualisationFields}
          onChange={onChange}
        />
      ) : null}
      {(chartType === 'bar' || hasMoreThanOneSeries) && <hr className={form.dashed} />}
      {chartType === 'bar' ? (
        <>
          <div className={form.formRow}>
            <ButtonGroup
              options={[
                { label: 'Vertical', value: 'vertical' },
                { label: 'Horizontal', value: 'horizontal' },
              ]}
              size="small"
              value={orientation}
              onChange={(value) => handleOrientationChange(value as VisualisationOrientation)}
            />
          </div>
        </>
      ) : null}
      {hasMoreThanOneSeries ? (
        <>
          <Toggle checked={isStacked} onChange={handleStackedChange} size="small">
            Stacked
          </Toggle>
          {isStacked ? (
            <Toggle
              checked={getVisualisationShowTotals(visualisationOptions)}
              onChange={handleShowTotalsChange}
              size="small">
              Show totals
            </Toggle>
          ) : null}
        </>
      ) : null}
    </div>
  );
};
